import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  List,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import pushNotificationService, {
  type ApprovalPushPreferences,
  type BrowserPushSubscriptionRow,
} from '@/services/pushNotification.service';

const { Text } = Typography;

type Props = {
  compact?: boolean;
  onPreferencesChange?: (prefs: ApprovalPushPreferences | null) => void;
};

/**
 * Browser push enablement, multi-device list, test send, quiet/business hours, and disable-all.
 * Permission is only requested after an explicit user click.
 */
export function BrowserPushManager({ compact, onPreferencesChange }: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState<ApprovalPushPreferences | null>(null);
  const [devices, setDevices] = useState<BrowserPushSubscriptionRow[]>([]);
  const [lastTestAt, setLastTestAt] = useState<string | null>(null);
  const [prefsForm] = Form.useForm();
  const supported = pushNotificationService.isSupported();
  const permission = pushNotificationService.permissionState();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const status = await pushNotificationService.getStatus();
      setSubscribed(status.subscribed);
      setPrefs(status.preferences);
      onPreferencesChange?.(status.preferences);
      prefsForm.setFieldsValue({
        quiet_hours_enabled: !!status.preferences?.quiet_hours_enabled,
        quiet_start: status.preferences?.quiet_start || '23:00',
        quiet_end: status.preferences?.quiet_end || '07:00',
        business_hours_only: !!status.preferences?.business_hours_only,
        business_start: status.preferences?.business_start || '08:00',
        business_end: status.preferences?.business_end || '18:00',
        timezone: status.preferences?.timezone || 'Asia/Dubai',
        critical_sound_enabled: status.preferences?.critical_sound_enabled !== false,
      });
      const list =
        status.subscriptions && status.subscriptions.length > 0
          ? status.subscriptions
          : await pushNotificationService.listSubscriptions();
      setDevices(list);
    } catch {
      setPrefs(null);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [onPreferencesChange, prefsForm]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      const ok = await pushNotificationService.subscribe();
      if (ok) {
        message.success('Browser push enabled for approval notifications');
        await refresh();
      } else {
        message.warning('Push permission was not granted or server is not configured');
      }
    } catch (e: unknown) {
      const detail = e instanceof Error ? e.message : 'Failed to enable browser push';
      message.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const disableAll = async () => {
    setBusy(true);
    try {
      await pushNotificationService.disableAll();
      message.success('All browser push devices disabled');
      await refresh();
    } catch {
      message.error('Failed to disable browser notifications');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    try {
      await pushNotificationService.revokeSubscription(id);
      message.success('Device revoked');
      await refresh();
    } catch {
      message.error('Failed to revoke device');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await pushNotificationService.sendTest();
      setLastTestAt(new Date().toISOString());
      message.success('Test notification sent');
    } catch {
      message.error('Test notification failed — enable push on this device first');
    } finally {
      setBusy(false);
    }
  };

  const saveTimingPrefs = async () => {
    setBusy(true);
    try {
      const values = await prefsForm.validateFields();
      const updated = await pushNotificationService.updatePreferences(values);
      setPrefs(updated);
      onPreferencesChange?.(updated);
      message.success('Notification timing preferences saved');
    } catch {
      message.error('Failed to save preferences');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Alert
        type="info"
        showIcon
        message="Browser notifications are unavailable in this browser or have been blocked in browser settings."
      />
    );
  }

  const permissionLabel =
    permission === 'granted' ? 'Allowed' : permission === 'denied' ? 'Blocked' : 'Not requested';

  const body = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Tag color={permission === 'granted' ? 'green' : permission === 'denied' ? 'red' : 'default'}>
          Permission: {permissionLabel}
        </Tag>
        <Tag color={subscribed ? 'green' : 'default'}>
          Device: {subscribed ? 'Registered' : 'Not registered'}
        </Tag>
        <Tag color={prefs?.browser_push_enabled ? 'blue' : 'default'}>
          Approval notifications: {prefs?.browser_push_enabled ? 'Enabled' : 'Off'}
        </Tag>
        {lastTestAt ? <Tag>Last test: {dayjs(lastTestAt).format('DD MMM HH:mm')}</Tag> : null}
      </Space>

      {permission === 'denied' ? (
        <Alert
          type="warning"
          showIcon
          message="Notifications are blocked in browser settings. Allow notifications for this site, then enable again."
        />
      ) : null}

      {!subscribed ? (
        <Alert
          type="info"
          showIcon
          message="Browser push is opt-in per device. Click Enable on each browser you want to receive OS notifications. In-app bell alerts still work without this."
        />
      ) : null}

      <Space wrap>
        <Button type="primary" loading={busy} disabled={permission === 'denied'} onClick={() => void enable()}>
          Enable browser notifications
        </Button>
        <Button loading={busy} disabled={!subscribed} onClick={() => void sendTest()}>
          Send test notification
        </Button>
        <Button danger loading={busy} disabled={!devices.some((d) => d.is_active)} onClick={() => void disableAll()}>
          Disable all browser notifications
        </Button>
      </Space>

      <Text type="secondary">
        Permission is requested only when you click Enable. You can register multiple browsers or devices.
      </Text>

      <Divider style={{ margin: '8px 0' }} />

      <Text strong>Quiet hours & business hours</Text>
      <Form form={prefsForm} layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item name="quiet_hours_enabled" label="Quiet hours" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Space wrap>
          <Form.Item name="quiet_start" label="Quiet start" style={{ marginBottom: 8 }}>
            <Input placeholder="23:00" style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="quiet_end" label="Quiet end" style={{ marginBottom: 8 }}>
            <Input placeholder="07:00" style={{ width: 100 }} />
          </Form.Item>
        </Space>
        <Form.Item
          name="business_hours_only"
          label="Business hours only (push → email outside window)"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Space wrap>
          <Form.Item name="business_start" label="Business start" style={{ marginBottom: 8 }}>
            <Input placeholder="08:00" style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="business_end" label="Business end" style={{ marginBottom: 8 }}>
            <Input placeholder="18:00" style={{ width: 100 }} />
          </Form.Item>
        </Space>
        <Form.Item name="timezone" label="Timezone" style={{ marginBottom: 8 }}>
          <Input placeholder="Asia/Dubai" />
        </Form.Item>
        <Form.Item name="critical_sound_enabled" label="Critical notification sound" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Button type="default" loading={busy} onClick={() => void saveTimingPrefs()}>
          Save timing preferences
        </Button>
      </Form>

      <List
        size="small"
        loading={loading}
        header={<Text strong>Registered devices</Text>}
        locale={{ emptyText: 'No browser devices registered yet.' }}
        dataSource={devices}
        renderItem={(item) => (
          <List.Item
            actions={[
              item.is_active ? (
                <Button key="rev" type="link" danger size="small" loading={busy} onClick={() => void revoke(item.id)}>
                  Revoke
                </Button>
              ) : (
                <Tag key="off">Disabled</Tag>
              ),
            ]}
          >
            <List.Item.Meta
              title={item.device_label || item.endpoint_hint || 'Browser'}
              description={
                <Text type="secondary">
                  {item.is_active ? 'Enabled' : 'Disabled'}
                  {item.last_used_at ? ` · Last used ${dayjs(item.last_used_at).format('DD MMM YYYY HH:mm')}` : ''}
                  {item.failure_count ? ` · Failures ${item.failure_count}` : ''}
                </Text>
              }
            />
          </List.Item>
        )}
      />
    </Space>
  );

  if (compact) return body;
  return (
    <Card size="small" title="Browser Notifications" loading={loading}>
      {body}
    </Card>
  );
}

export default BrowserPushManager;
